// Contacts API — typed-обёртки над 7 endpoints /api/contacts/*.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { t } from '@/i18n';
import type { Page } from './tasks';

/** DTO контакта. */
export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  /** Заметки о контакте (backend TEXT nullable, ≤5000 chars). */
  notes: string | null;
  companyId: string | null;
  ownerUserId: string | null;
  departmentId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ContactFilters {
  page?: number;
  size?: number;
  ownerUserId?: string;
  departmentId?: string;
  companyId?: string;
  /** Живой поиск: ILIKE по выбранным полям (R33a backend, R33c frontend; legacy `search` backend игнорировал). */
  q?: string;
  /**
   * CSV-список полей поиска (U-34-D, SSOT §3.3): first_name|last_name|email|phone|company_id
   * (company_id → подзапрос по названию компании). Отсутствует/пусто → все поля.
   */
  fields?: string;
  /** Сортировка «field,direction» (U-23: whitelist createdAt|updatedAt; направление asc|desc). */
  sort?: string;
}

export interface ContactCreateRequest {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  /** Заметки (optional, ≤5000 chars). */
  notes?: string | null;
  companyId?: string | null;
  ownerUserId?: string | null;
  departmentId: string;
}

export interface ContactUpdateRequest {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  /** Заметки (optional, ≤5000 chars; null → очистить). */
  notes?: string | null;
  companyId?: string | null;
  ownerUserId?: string | null;
  expectedUpdatedAt: string;
}

export interface ContactAssignRequest {
  ownerUserId: string;
}

/** Contacts API. */
export const contactsApi = {
  /** GET /api/contacts?filters → Page<Contact>. */
  async list(filters: ContactFilters = {}): Promise<Page<Contact>> {
    const { data } = await apiClient.get<Page<Contact>>('/api/contacts', { params: filters });
    return data;
  },

  /** GET /api/contacts/{id} → Contact. */
  async get(id: string): Promise<Contact> {
    const { data } = await apiClient.get<Contact>(`/api/contacts/${id}`);
    return data;
  },

  /** POST /api/contacts → Contact. */
  async create(payload: ContactCreateRequest): Promise<Contact> {
    const { data } = await apiClient.post<Contact>('/api/contacts', payload);
    return data;
  },

  /** PUT /api/contacts/{id} → Contact. */
  async update(id: string, payload: ContactUpdateRequest): Promise<Contact> {
    const { data } = await apiClient.put<Contact>(`/api/contacts/${id}`, payload);
    return data;
  },

  /** DELETE /api/contacts/{id}. */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/contacts/${id}`);
  },

  /** POST /api/contacts/{id}/assign → Contact. */
  async assign(id: string, payload: ContactAssignRequest): Promise<Contact> {
    const { data } = await apiClient.post<Contact>(`/api/contacts/${id}/assign`, payload);
    return data;
  },

  /** GET /api/contacts/{id}/deals → Page<Deal>. Backend возвращает raw array (баг D25) — нормализуем. */
  async listDealsByContact(contactId: string, page = 0, size = 50): Promise<Page<unknown>> {
    const { data } = await apiClient.get<Page<unknown> | unknown[]>(`/api/contacts/${contactId}/deals`, {
      params: { page, size },
    });
    return normalizeCrossEntityPage(data);
  },
};

/**
 * Нормализует ответ cross-entity endpoint: backend возвращает raw array вместо Page (R25 / баг D25 — доказано curl'ом).
 * `Array.isArray(data) ? { content: data, total: data.length, page: 0, size: data.length } : data`.
 */
function normalizeCrossEntityPage<T>(data: Page<T> | T[]): Page<T> {
  if (Array.isArray(data)) {
    return { content: data, total: data.length, page: 0, size: data.length };
  }
  return data;
}

/**
 * Сколько контактов запрашиваем для построения map.
 * Backend валидирует size ≤ 100 (ContactListQuery), 500 давал HTTP 500 (R23 / баг D21).
 */
const CONTACTS_PAGE_SIZE = 100;

/** staleTime для contacts-map: справочник меняется редко. */
const CONTACTS_STALE_TIME_MS = 5 * 60 * 1000;

/** Полное имя контакта («Имя Фамилия»). */
export function contactDisplayName(contact: Contact): string {
  return `${contact.firstName} ${contact.lastName}`.trim();
}

/** Строит Map id → «Имя Фамилия» из Page<Contact>. */
export function buildContactMap(page: Page<Contact>): Map<string, string> {
  return new Map(page.content.map((c) => [c.id, contactDisplayName(c)]));
}

/**
 * Резолвит id контакта в полное имя.
 * <p>
 * B2 (pintest-fix-B): fallback «короткий UUID» удалён — неизвестный id или
 * незагруженная map (loading/error) даёт {@code t('common.unknown')} = «Неизвестно».
 * null → «—».
 */
export function resolveContactName(
  contactMap: Map<string, string> | undefined,
  contactId: string | null | undefined,
): string {
  if (!contactId) return '—';
  return contactMap?.get(contactId) ?? t('common.unknown');
}

/** TanStack Query hook: Map<id, «Имя Фамилия»> для резолва имён контактов. */
export function useContactMap(): Map<string, string> | undefined {
  const query = useQuery({
    queryKey: ['contacts-map'],
    queryFn: () => contactsApi.list({ page: 0, size: CONTACTS_PAGE_SIZE }),
    staleTime: CONTACTS_STALE_TIME_MS,
    select: buildContactMap,
    retry: false,
  });
  return query.data;
}

/**
 * TanStack Query hook: Page<Contact> (первые {@code CONTACTS_PAGE_SIZE}) — для select'ов форм,
 * где нужны поля контакта (companyId-фильтр в DealNewPage), а не только имя (R26 / баг D26).
 * Разделяет queryKey/queryFn с {@link useContactMap} — кэш общий, select применяется per-observer.
 */
export function useContactsPage(): Page<Contact> | undefined {
  const query = useQuery({
    queryKey: ['contacts-map'],
    queryFn: () => contactsApi.list({ page: 0, size: CONTACTS_PAGE_SIZE }),
    staleTime: CONTACTS_STALE_TIME_MS,
    retry: false,
  });
  return query.data;
}

/**
 * TanStack Query hook: контакт по id (для форм, где назначенный id может быть вне page-100, U-28).
 * Единый key `['contact', id]` — общий кэш с карточкой контакта/вкладкой DealDetailPage.
 * retry: false — формы не держат пользователя в ожидании при ошибке справочника (INFO-аспект ТЗ).
 */
export function useContact(id: string | null | undefined) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => contactsApi.get(id ?? ''),
    enabled: Boolean(id),
    staleTime: CONTACTS_STALE_TIME_MS,
    retry: false,
  });
}
